import { useEffect, useState } from 'react';

// Google OAuth configuration constants for Gmail access
// Using the same client ID as Google Calendar
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Debug log to check if client ID is loaded
console.log('Email Auth - Google Client ID:', GOOGLE_CLIENT_ID ? 'Loaded' : 'Missing');

// Include Gmail-specific scopes for accessing emails
// Note: We don't need a separate scope for attachments since gmail.readonly includes access to attachments
const EMAIL_SCOPES = [
  'https://www.googleapis.com/auth/gmail.readonly', // Read-only access to Gmail (includes attachments)
  'https://www.googleapis.com/auth/gmail.modify', // Needed for marking emails as read/processed
  'openid',
  'profile',
  'email',
  // User info scopes
  'https://www.googleapis.com/auth/userinfo.profile',
  'https://www.googleapis.com/auth/userinfo.email'
];

// Hook to manage Email Auth state
export const useEmailAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTimer, setRefreshTimer] = useState(null);

  // Initialize Google Auth for Gmail on component mount
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initializeEmailAuth;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
      // Clear refresh timer on unmount
      if (refreshTimer) {
        if (refreshTimer.main) {
          clearTimeout(refreshTimer.main);
        }
        if (refreshTimer.monitoring) {
          refreshTimer.monitoring.forEach(timer => clearTimeout(timer));
        }
      }
    };
  }, []);

  // Schedule automatic token refresh before expiry
  const scheduleTokenRefresh = (expiresIn) => {
    // Clear any existing timer
    if (refreshTimer) {
      clearTimeout(refreshTimer);
    }
    
    // Schedule refresh at 45 minutes (75% of typical 60-minute token lifetime)
    // This gives us a buffer in case the refresh fails
    const refreshTime = Math.min(expiresIn * 0.75, 45 * 60) * 1000; // Convert to milliseconds
    const expiryTime = new Date(Date.now() + (expiresIn * 1000));
    
    console.log(`🔄 [Email/Gmail] Token refresh scheduled:`);
    console.log(`  - Current time: ${new Date().toLocaleTimeString()}`);
    console.log(`  - Token expires at: ${expiryTime.toLocaleTimeString()} (in ${expiresIn / 60} minutes)`);
    console.log(`  - Refresh scheduled for: ${new Date(Date.now() + refreshTime).toLocaleTimeString()} (in ${refreshTime / 1000 / 60} minutes)`);
    
    // Set up monitoring intervals
    const monitoringIntervals = [];
    
    // Monitor at 15 minutes before expiry
    const fifteenMinCheck = expiresIn > 15 * 60 ? (expiresIn - 15 * 60) * 1000 : null;
    if (fifteenMinCheck && fifteenMinCheck > 0) {
      monitoringIntervals.push(setTimeout(() => {
        console.log(`⏰ [Email/Gmail] 15 minutes until token expiry:`);
        console.log(`  - Current time: ${new Date().toLocaleTimeString()}`);
        console.log(`  - Token expires at: ${expiryTime.toLocaleTimeString()}`);
        console.log(`  - Refresh will occur in ${((refreshTime - fifteenMinCheck) / 1000 / 60).toFixed(1)} minutes`);
      }, fifteenMinCheck));
    }
    
    // Monitor at 5 minutes before expiry
    const fiveMinCheck = expiresIn > 5 * 60 ? (expiresIn - 5 * 60) * 1000 : null;
    if (fiveMinCheck && fiveMinCheck > 0) {
      monitoringIntervals.push(setTimeout(() => {
        console.log(`⚠️ [Email/Gmail] 5 minutes until token expiry:`);
        console.log(`  - Current time: ${new Date().toLocaleTimeString()}`);
        console.log(`  - Token expires at: ${expiryTime.toLocaleTimeString()}`);
        console.log(`  - Refresh will occur in ${((refreshTime - fiveMinCheck) / 1000 / 60).toFixed(1)} minutes`);
      }, fiveMinCheck));
    }
    
    const timer = setTimeout(async () => {
      console.log(`🔄 [Email/Gmail] Starting automatic token refresh...`);
      console.log(`  - Current time: ${new Date().toLocaleTimeString()}`);
      console.log(`  - Original expiry: ${expiryTime.toLocaleTimeString()}`);
      
      try {
        const newToken = await refreshToken();
        if (newToken) {
          console.log(`✅ [Email/Gmail] Token refreshed successfully!`);
          console.log(`  - Refresh completed at: ${new Date().toLocaleTimeString()}`);
          console.log(`  - New token acquired, next refresh scheduled`);
        } else {
          console.error(`❌ [Email/Gmail] Token refresh failed!`);
          console.error(`  - Time of failure: ${new Date().toLocaleTimeString()}`);
          console.error(`  - User will need to re-authenticate`);
        }
      } catch (error) {
        console.error(`❌ [Email/Gmail] Error during token refresh:`);
        console.error(`  - Time of error: ${new Date().toLocaleTimeString()}`);
        console.error(`  - Error details:`, error);
        console.error(`  - User will need to re-authenticate`);
      }
    }, refreshTime);
    
    // Store all timers so we can clear them if needed
    setRefreshTimer({ main: timer, monitoring: monitoringIntervals });
  };
  
  // Create a function to persist authentication data for extended periods
  const storeAuthPermanently = (tokenInfo, userInfo) => {
    try {
      // Store the actual token expiry time
      const actualExpiry = new Date();
      const expiresIn = tokenInfo.expires_in || 3600; // Default to 1 hour if not provided
      actualExpiry.setSeconds(actualExpiry.getSeconds() + expiresIn);
      
      // Store the token and actual expiry
      localStorage.setItem('emailAccessToken', tokenInfo.access_token);
      localStorage.setItem('emailTokenExpiry', actualExpiry.getTime().toString());
      localStorage.setItem('emailTokenExpiresIn', expiresIn.toString());
      
      // Store other token info
      if (tokenInfo.scope) {
        localStorage.setItem('emailScope', tokenInfo.scope);
      }
      
      // Store refresh token if available
      if (tokenInfo.refresh_token) {
        localStorage.setItem('emailRefreshToken', tokenInfo.refresh_token);
      }
      
      // Store user info if available
      if (userInfo) {
        localStorage.setItem('emailUser', JSON.stringify(userInfo));
      }
      
      // Store a flag indicating we're using automatic refresh
      localStorage.setItem('emailAutoRefresh', 'true');
      
      console.log(`Email auth stored with actual expiry (${expiresIn} seconds)`);
      
      // Schedule automatic refresh
      scheduleTokenRefresh(expiresIn);
    } catch (error) {
      console.error('Error storing email auth data:', error);
    }
  };
  
  const initializeEmailAuth = async () => {
    // Check if we have stored token
    const storedToken = localStorage.getItem('emailAccessToken');
    const storedTokenExpiry = localStorage.getItem('emailTokenExpiry');
    const storedTokenExpiresIn = localStorage.getItem('emailTokenExpiresIn');
    const storedUser = localStorage.getItem('emailUser');
    const hasAutoRefresh = localStorage.getItem('emailAutoRefresh') === 'true';

    if (storedToken && storedTokenExpiry) {
      const now = new Date().getTime();
      const expiry = parseInt(storedTokenExpiry);
      
      if (now < expiry) {
        // Token is still valid
        console.log('Restoring Email auth session from localStorage');
        setAccessToken(storedToken);
        try {
          if (storedUser) {
            setUser(JSON.parse(storedUser));
          }
        } catch (e) {
          console.error('Error parsing stored email user:', e);
        }
        setIsAuthenticated(true);
        setIsLoading(false);
        
        // Schedule refresh for the remaining time
        const remainingTime = (expiry - now) / 1000; // Convert to seconds
        console.log(`Email token expires in ${remainingTime / 60} minutes`);
        scheduleTokenRefresh(remainingTime);
      } else if (hasAutoRefresh) {
        // Token has expired but we have auto-refresh enabled
        console.log('Stored email token has expired, attempting silent refresh...');
        setIsLoading(true);
        
        // Set temporary auth state to attempt refresh
        setAccessToken(storedToken);
        setIsAuthenticated(true);
        if (storedUser) {
          try {
            setUser(JSON.parse(storedUser));
          } catch (e) {
            console.error('Error parsing stored email user:', e);
          }
        }
        
        // Try to refresh the token silently
        try {
          const newToken = await refreshToken();
          if (newToken) {
            console.log('Successfully refreshed expired email token on initialization');
            setIsLoading(false);
          } else {
            console.log('Failed to refresh email token, clearing auth state');
            clearEmailAuthData();
            setIsAuthenticated(false);
            setAccessToken(null);
            setUser(null);
            setIsLoading(false);
          }
        } catch (error) {
          console.error('Error refreshing email token on init:', error);
          clearEmailAuthData();
          setIsAuthenticated(false);
          setAccessToken(null);
          setUser(null);
          setIsLoading(false);
        }
      } else {
        // Token expired and no auto-refresh
        console.log('Email auth has expired, clearing');
        clearEmailAuthData();
        setIsLoading(false);
      }
    } else {
      // No token exists
      setIsLoading(false);
      setIsAuthenticated(false);
      setAccessToken(null);
      setUser(null);
    }

    // Initialize Google One Tap (not using it but required for OAuth flow)
    window.google?.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleCredentialResponse,
    });
    
    console.log('Email auth initialized');
  };

  const handleCredentialResponse = (response) => {
    if (response.credential) {
      // Parse the JWT token to get user info
      const payload = parseJwt(response.credential);
      
      // Set authentication state
      setAccessToken(response.credential);
      setUser({
        name: payload.name,
        email: payload.email,
        picture: payload.picture,
      });
      setIsAuthenticated(true);

      // Store in localStorage with expiry
      const expiryTime = new Date().getTime() + (payload.exp * 1000);
      localStorage.setItem('emailAccessToken', response.credential);
      localStorage.setItem('emailTokenExpiry', expiryTime.toString());
      localStorage.setItem('emailUser', JSON.stringify({
        name: payload.name,
        email: payload.email,
        picture: payload.picture,
      }));
    }
  };

  const signIn = () => {
    console.log('Starting Google OAuth flow for Gmail with scopes:', EMAIL_SCOPES);
    
    // Clear any existing tokens before signing in again to ensure a fresh start
    clearEmailAuthData();
    setIsAuthenticated(false);
    setAccessToken(null);
    setUser(null);
    
    if (window.google?.accounts.oauth2) {
      try {
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: EMAIL_SCOPES.join(' '),
          prompt: 'consent',  // Always force consent screen
          include_granted_scopes: true, // Include previously granted scopes
          // Request offline access to get refresh tokens
          access_type: 'offline',
          callback: async (tokenResponse) => {
            if (tokenResponse.access_token) {
              const expiresIn = tokenResponse.expires_in || 3600;
              console.log(`🎉 [Email/Gmail] Initial authentication successful:`);
              console.log(`  - Time: ${new Date().toLocaleTimeString()}`);
              console.log(`  - Token expires in: ${expiresIn / 60} minutes`);
              console.log(`  - Expiry time: ${new Date(Date.now() + expiresIn * 1000).toLocaleTimeString()}`);
              console.log(`  - Scopes granted: ${tokenResponse.scope}`);
              
              // Update component state
              setAccessToken(tokenResponse.access_token);
              setIsAuthenticated(true);
              
              // Get user info first
              try {
                const userInfo = await fetchUserInfoAsync(tokenResponse.access_token);
                
                // Store with automatic refresh
                storeAuthPermanently(tokenResponse, userInfo);
                
                // Update state with user info
                setUser(userInfo);
                
                console.log(`✅ [Email/Gmail] Setup complete with automatic refresh enabled`);
                console.log(`  - Token stored in localStorage: ${!!localStorage.getItem('emailAccessToken')}`);
              } catch (error) {
                console.error('Error fetching Gmail user info:', error);
                
                // Fall back to basic storage without user info
                storeAuthPermanently(tokenResponse, null);
                console.log('Connected to Gmail (without user info)');
              }
            } else {
              console.error('No access token returned for Gmail');
              alert('Failed to get access to your Gmail. Please try again.');
            }
          },
          error_callback: (error) => {
            console.error('Google OAuth error for Gmail:', error);
            alert('Failed to sign in with Google for email access: ' + (error.error || error.message || 'Unknown error'));
          }
        });
        
        // Request access token with all scopes
        tokenClient.requestAccessToken({
          prompt: 'consent',
          access_type: 'offline',
        });
      } catch (error) {
        console.error('Error initializing Google OAuth for Gmail:', error);
        alert('Error setting up Gmail authentication. Please try again later.');
      }
    } else {
      console.error('Google OAuth2 not initialized for Gmail');
      alert('Gmail authentication not available. Please try again later. Make sure you\'re not blocking third-party cookies.');
    }
  };
  
  // Helper function that returns a promise with user info
  const fetchUserInfoAsync = async (token) => {
    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        return await response.json();
      }
      throw new Error('Failed to fetch Gmail user info');
    } catch (error) {
      console.error('Error fetching Gmail user info:', error);
      throw error;
    }
  };

  const signOut = () => {
    if (window.google?.accounts.id) {
      window.google.accounts.id.disableAutoSelect();
    }
    
    // Clear all email auth data from local storage
    clearEmailAuthData();
    
    // Reset state
    setAccessToken(null);
    setUser(null);
    setIsAuthenticated(false);
    
    console.log('Gmail account signed out');
  };

  const clearEmailAuthData = () => {
    localStorage.removeItem('emailAccessToken');
    localStorage.removeItem('emailTokenExpiry');
    localStorage.removeItem('emailTokenExpiresIn');
    localStorage.removeItem('emailUser');
    localStorage.removeItem('emailScope');
    localStorage.removeItem('emailRefreshToken');
    localStorage.removeItem('emailExtendedAuth');
    localStorage.removeItem('emailOriginalExpiry');
    localStorage.removeItem('emailAutoRefresh');
    
    // Clear refresh timer
    if (refreshTimer) {
      if (refreshTimer.main) {
        clearTimeout(refreshTimer.main);
      }
      if (refreshTimer.monitoring) {
        refreshTimer.monitoring.forEach(timer => clearTimeout(timer));
      }
      setRefreshTimer(null);
    }
  };

  // Helper to parse JWT token
  const parseJwt = (token) => {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
      return null;
    }
  };

  // Add token refresh functionality
  const refreshToken = async () => {
    const refreshTokenValue = localStorage.getItem('emailRefreshToken');
    
    if (!refreshTokenValue) {
      console.log('No refresh token available. This is expected for tokens obtained through OAuth2 implicit flow.');
      // Even without a refresh token, we'll try the silent flow
    }
    
    try {
      // Since we can't directly refresh the token from the client side without exposing client secret,
      // we'll attempt a silent sign-in process with existing grants
      console.log('Attempting to silently refresh email token...');
      
      if (window.google?.accounts.oauth2) {
        return new Promise((resolve, reject) => {
          const tokenClient = window.google.accounts.oauth2.initTokenClient({
            client_id: GOOGLE_CLIENT_ID,
            scope: EMAIL_SCOPES.join(' '),
            prompt: 'none',  // 'none' for silent auth - will fail if user hasn't previously consented
            include_granted_scopes: true,
            callback: (tokenResponse) => {
              if (tokenResponse.access_token) {
                const expiresIn = tokenResponse.expires_in || 3600;
                console.log(`🔄 [Email/Gmail] Silent token refresh successful:`);
                console.log(`  - Time: ${new Date().toLocaleTimeString()}`);
                console.log(`  - New token expires in: ${expiresIn / 60} minutes`);
                console.log(`  - Expiry time: ${new Date(Date.now() + expiresIn * 1000).toLocaleTimeString()}`);
                
                // Set the new access token in state
                setAccessToken(tokenResponse.access_token);
                setIsAuthenticated(true);
                
                // Update localStorage with actual expiry
                const actualExpiry = new Date();
                actualExpiry.setSeconds(actualExpiry.getSeconds() + expiresIn);
                
                localStorage.setItem('emailAccessToken', tokenResponse.access_token);
                localStorage.setItem('emailTokenExpiry', actualExpiry.getTime().toString());
                localStorage.setItem('emailTokenExpiresIn', expiresIn.toString());
                
                // Store refresh token if provided
                if (tokenResponse.refresh_token) {
                  console.log('  - New refresh token received and stored');
                  localStorage.setItem('emailRefreshToken', tokenResponse.refresh_token);
                }
                
                // Force update the token validation timestamp
                localStorage.setItem('emailTokenLastValidated', new Date().getTime().toString());
                
                // Schedule next refresh
                scheduleTokenRefresh(expiresIn);
                
                // Resolve with the new token
                resolve(tokenResponse.access_token);
              } else {
                console.error('Failed to refresh email token silently - no access token returned');
                reject(new Error('Failed to refresh email token'));
              }
            },
            error_callback: (error) => {
              console.error(`❌ [Email/Gmail] Silent refresh failed:`, error);
              if (error.type === 'popup_failed_to_open' || error.type === 'popup_closed') {
                console.error(`  - Popup was blocked or closed`);
              } else if (error.type === 'access_denied') {
                console.error(`  - User needs to re-authenticate (access denied)`);
              } else if (error.type === 'immediate_failed') {
                console.error(`  - Silent auth not possible (user not previously authenticated)`);
              }
              console.error(`  - Error type: ${error.type}`);
              console.error(`  - Full error:`, error);
              reject(error);
            }
          });
          
          // Try to get a new access token silently
          try {
            console.log('Requesting access token silently...');
            tokenClient.requestAccessToken({
              prompt: 'none', // 'none' for truly silent auth
              hint: user?.email, // Provide user hint if available
            });
          } catch (requestError) {
            console.error('Error requesting access token:', requestError);
            reject(requestError);
          }
        });
      } else {
        throw new Error('Google OAuth is not initialized for Gmail');
      }
    } catch (error) {
      console.error('Error refreshing email token:', error);
      return null;
    }
  };

  return {
    isAuthenticated,
    accessToken,
    user,
    isLoading,
    signIn,
    signOut,
    refreshToken,
  };
};